import React, { useState, useEffect, useMemo } from "react"
import { Modal, Radio, Input, Button, Flex, Form } from "antd"
import styles from "./Notify.module.css"
import { useTranslation } from "react-i18next"
import { validatePhoneNumber, PhoneInput } from "ui/Input/PhoneInput"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import PropTypes from "prop-types"

export const NotifyModal = ({ visible, onClose, onSend, loading, guestData }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [sendMethod, setSendMethod] = useState("email")
  const [phoneValue, setPhoneValue] = useState("")

  const isPrimaryGuest = !!guestData?.is_primary_guest

  // Resolve the email/phone/default method once from guestData so the same values
  // are reused on initial population and on every send-method switch.
  const { resolvedEmail, resolvedPhone, defaultMethod } = useMemo(() => {
    const notifyEmail = guestData?.notify_details?.email || ""
    const notifyPhone = guestData?.notify_details?.phone_number || ""

    if (guestData?.share_by === "email") {
      return {
        resolvedEmail: notifyEmail || guestData?.shared_email || "",
        resolvedPhone: "",
        defaultMethod: "email"
      }
    }

    if (guestData?.share_by === "text") {
      return {
        resolvedEmail: "",
        resolvedPhone: notifyPhone || guestData?.shared_phone || "",
        defaultMethod: "text"
      }
    }

    const email = isPrimaryGuest
      ? notifyEmail || guestData?.guest_email || guestData?.shared_email || ""
      : notifyEmail || guestData?.shared_email || ""
    const phone = isPrimaryGuest
      ? notifyPhone || guestData?.phone_number || guestData?.shared_phone || ""
      : notifyPhone || guestData?.shared_phone || ""

    return {
      resolvedEmail: email,
      resolvedPhone: phone,
      defaultMethod: phone && !email ? "text" : "email"
    }
  }, [guestData, isPrimaryGuest])

  useEffect(() => {
    if (!visible) return

    setSendMethod(defaultMethod)
    setPhoneValue(resolvedPhone)
    form.setFieldsValue({
      sendMethod: defaultMethod,
      email: resolvedEmail,
      phoneNumber: { fullNumber: resolvedPhone, countryCode: "US" }
    })
  }, [visible, resolvedEmail, resolvedPhone, defaultMethod, form])

  const handleSend = () => {
    form.validateFields().then((values) => {
      if (onSend) {
        onSend({ ...values, sendMethod })
      }
    })
  }

  const handleCancel = () => {
    form.resetFields()
    setSendMethod("email")
    if (onClose) {
      onClose()
    }
  }

  const handleSendMethodChange = (e) => {
    const newMethod = e.target.value
    setSendMethod(newMethod)
    // Restore the resolved values for the selected method so the form value stays
    // in sync with what the inputs display (prevents false "required" validation
    // and prevents the email going blank after switching away and back).
    setPhoneValue(resolvedPhone)
    form.setFieldsValue({
      email: resolvedEmail,
      phoneNumber: { fullNumber: resolvedPhone, countryCode: "US" }
    })
  }

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={480}
      className={styles.shareWaiverModal}
      closeIcon={<span className={styles.closeIcon}>×</span>}
      centered
      maskClosable={false}
    >
      <Form form={form} layout="vertical">
        <div className="mb-xs">
          <h2 className={styles.modalTitle}>{t("digital_waiver_and_contracts.notify_guest")}</h2>
        </div>

        {/* Radio Buttons */}
        <div className="mb-xs">
          <Form.Item name="sendMethod" initialValue={sendMethod}>
            <Radio.Group onChange={handleSendMethodChange} className={styles.radioGroup}>
              <Radio value="text" className={styles.radio}>
                {t("embedded.booking_confirmation.send_by_text")}
              </Radio>
              <Radio value="email" className={styles.radio}>
                {t("embedded.booking_confirmation.send_by_email")}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        {/* Phone Number Input */}
        {sendMethod === "text" && (
          <div className="mb-l">
            <Form.Item
              name="phoneNumber"
              label="Phone Number*"
              rules={
                isPrimaryGuest
                  ? []
                  : [
                      {
                        validator: (_, value) => {
                          if (!value?.fullNumber?.trim()) {
                            return Promise.reject("Please enter phone number")
                          }
                          return validatePhoneNumber(value.fullNumber)
                        }
                      }
                    ]
              }
            >
              <PhoneInput
                key={phoneValue}
                initialValue={phoneValue}
                // disabled={isPrimaryGuest}
                onChange={(value) => {
                  if (!isPrimaryGuest) form.setFieldValue("phoneNumber", value)
                }}
              />
            </Form.Item>
          </div>
        )}

        {/* Email Input */}
        {sendMethod === "email" && (
          <div className="mb-xs">
            <LabeledField
              name="email"
              labelKey="Email Address*"
              rules={
                isPrimaryGuest
                  ? []
                  : [
                      { required: true, message: "Please enter email address" },
                      { type: "email", message: "Please enter a valid email" }
                    ]
              }
            >
              <Input placeholder="Enter email address" className={styles.emailInput}
                // disabled={isPrimaryGuest}
              />
            </LabeledField>
          </div>
        )}

        {/* Footer */}
        <Flex justify="end" align="center">
          <Button type="primary" loading={loading} disabled={loading} onClick={handleSend}>
            {t("digital_waiver_and_contracts.notify")}
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}

NotifyModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSend: PropTypes.func,
  loading: PropTypes.bool,
  guestData: PropTypes.shape({
    is_primary_guest: PropTypes.bool,
    share_by: PropTypes.string,
    shared_email: PropTypes.string,
    shared_phone: PropTypes.string,
    guest_email: PropTypes.string,
    phone_number: PropTypes.string,
    notify_details: PropTypes.shape({
      email: PropTypes.string,
      phone_number: PropTypes.string
    })
  })
}
